import smartZoneIcon from '../assets/icons/smartZoneIcon.svg';
import { isSmartZoneCapable } from '../objects/SmartZoneBehaviors';
import { type SmartZoneFormData } from '../shared-types';
import { svgToIcon } from '../utils/svg';
import Tool, {
  type IToolActionInput,
  type IToolAvailableInput,
  type IToolEnabledInput,
  simpleToolButton,
} from './Tool';

export const toggleSmartZone = (
  obj: fabric.Object,
  enabled: boolean,
  formData?: SmartZoneFormData
) => {
  if (isSmartZoneCapable(obj)) {
    const existingConfig = obj.smartZoneConfig ?? { enabled: false };
    existingConfig.configurations ??= {};

    // if only one record type is available, enable it by default
    if (
      enabled &&
      formData?.length === 1 &&
      !existingConfig.configurations?.[formData[0].recordType]?.enabled
    ) {
      existingConfig.configurations[formData[0].recordType] = {
        enabled: true,
      };
    }

    obj.set('smartZoneConfig', {
      ...existingConfig,
      enabled,
    });
    obj.canvas?.controller.actionRerenderToolbar();
    obj.canvas?.requestRenderAll();
  }
};

class ToolEnableSmartZone extends Tool {
  static toolName = 'Enable smart zone';

  static available({ selection, controller }: IToolAvailableInput): boolean {
    // Only available when smart zone is disabled or not configured
    return Boolean(
      controller.smartZonesEnabled() &&
        selection &&
        isSmartZoneCapable(selection) &&
        !selection.smartZoneConfig?.enabled
    );
  }

  static enabled({ selection }: IToolEnabledInput): boolean {
    return !selection?.isLocked;
  }

  static action({ selection, controller }: IToolActionInput): void {
    toggleSmartZone(selection, true, controller.props.smartZoneFormData);
    controller.actionOpenSidePanel('smartZone');
  }

  static buttonComponent() {
    return simpleToolButton(ToolEnableSmartZone, svgToIcon(smartZoneIcon));
  }
}

const available = ({ selection, controller }: IToolAvailableInput) =>
  Boolean(
    controller.smartZonesEnabled() &&
      selection &&
      isSmartZoneCapable(selection) &&
      selection.smartZoneConfig?.enabled
  );

class ToolDisableSmartZone extends Tool {
  static toolName = 'Disable smart zone';

  static available(attrs: IToolAvailableInput): boolean {
    return available(attrs);
  }

  static enabled({ selection }: IToolEnabledInput): boolean {
    return !selection?.isLocked;
  }

  static action({ selection }: IToolActionInput): void {
    toggleSmartZone(selection, false);
  }

  static buttonComponent() {
    return simpleToolButton(ToolDisableSmartZone, 'Disable smart zone');
  }
}

class ToolEditSmartZone extends Tool {
  static toolName = 'Edit smart zone';

  static available(attrs: IToolAvailableInput): boolean {
    return available(attrs);
  }

  static enabled({ selection }: IToolEnabledInput): boolean {
    return !selection?.isLocked;
  }

  static action({ controller }: IToolActionInput): void {
    // this will detect the selection and automatically open the editor for this smart zone
    controller.actionOpenSidePanel('smartZone');
  }

  static buttonComponent() {
    return simpleToolButton(ToolEditSmartZone, svgToIcon(smartZoneIcon));
  }
}

export { ToolDisableSmartZone, ToolEditSmartZone, ToolEnableSmartZone };
